import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  FileText,
  Users,
  Download,
  Trash2,
  LogOut,
  Plus,
  X,
  Loader2,
  Database,
  BarChart3,
  Pencil,
  Newspaper,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import API from "@/lib/api";

const datasetCategories = [
  { value: "stem-reasoning", label: "A — STEM Reasoning & Problem Solving" },
  { value: "language-literacy", label: "B — Language, Literacy & Comprehension" },
  { value: "social-sciences", label: "C — Social Sciences, Civics & General Knowledge" },
  { value: "higher-education", label: "D — Higher Education & Professional Knowledge" },
];

const getToken = () => localStorage.getItem("admin_token") || "";

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [role, setRole] = useState(localStorage.getItem("admin_role") || "viewer");
  const [loggedInUser, setLoggedInUser] = useState(localStorage.getItem("admin_user") || "");
  const isAdmin = role === "admin";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [logs, setLogs] = useState([]);

  const [activeTab, setActiveTab] = useState("files");
  const [files, setFiles] = useState([]);
  const [leads, setLeads] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [stats, setStats] = useState({ leads_count: 0, files_count: 0, total_downloads: 0 });
  const [isLoading, setIsLoading] = useState(false);
  
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploadData, setUploadData] = useState({
    file: null,
    dataset_category: "",
    description: "",
    is_sample: true
  });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  // All-leads filter + notes state
  const [allLeadsFilter, setAllLeadsFilter] = useState({ search: "", lead_type: "", source: "", status: "", country: "", campaign: "", platform: "", wa: "" });
  const [allLeadsSort, setAllLeadsSort] = useState("created_desc");
  const [isSyncingVobiz, setIsSyncingVobiz] = useState(false);
  const [unmatchedConvs, setUnmatchedConvs] = useState([]);
  const [isLoadingUnmatched, setIsLoadingUnmatched] = useState(false);
  const [noteOpen, setNoteOpen] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [isBlogFormOpen, setIsBlogFormOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isSavingPost, setIsSavingPost] = useState(false);
  const emptyBlogForm = {
    title: "", slug: "", excerpt: "", content: "",
    tags: "", category: "Research", author: "Nalanda Data Team",
    published: false, hf_model_ref: "",
  };
  const [blogForm, setBlogForm] = useState(emptyBlogForm);

  // Verify session token with backend on page load
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/admin/verify`, { headers: { "X-Admin-Token": token } })
      .then(res => {
        setRole(res.data.role);
        setLoggedInUser(res.data.username);
        localStorage.setItem("admin_role", res.data.role);
        localStorage.setItem("admin_user", res.data.username);
        setIsAuthenticated(true);
      })
      .catch(() => {
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_auth");
        localStorage.removeItem("admin_role");
        localStorage.removeItem("admin_user");
      });
  }, []);

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [filesRes, leadsRes, statsRes, blogRes, vendorsRes, allLeadsRes] = await Promise.all([
        axios.get(`${API}/files`),
        axios.get(`${API}/leads`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/blog/all`),
        axios.get(`${API}/vendors`, { headers: authHeaders() }),
        axios.get(`${API}/all-leads`, { headers: authHeaders() }),
      ]);
      setFiles(filesRes.data);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setBlogPosts(blogRes.data);
      setVendors(vendorsRes.data);
      setAllLeads(allLeadsRes.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const slugify = (text) =>
    text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const openNewPost = () => {
    setEditingPost(null);
    setBlogForm(emptyBlogForm);
    setIsBlogFormOpen(true);
  };

  const openEditPost = (post) => {
    setEditingPost(post);
    setBlogForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      tags: (post.tags || []).join(", "),
      category: post.category,
      author: post.author,
      published: post.published,
      hf_model_ref: post.hf_model_ref || "",
    });
    setIsBlogFormOpen(true);
  };

  const handleSavePost = async (e) => {
    e.preventDefault();
    if (!blogForm.title || !blogForm.slug || !blogForm.content) {
      toast.error("Title, slug, and content are required");
      return;
    }
    setIsSavingPost(true);
    const payload = {
      ...blogForm,
      tags: blogForm.tags ? blogForm.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
      hf_model_ref: blogForm.hf_model_ref || null,
    };
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, payload);
        toast.success("Post updated");
      } else {
        await axios.post(`${API}/blog`, payload);
        toast.success("Post created");
      }
      setIsBlogFormOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save post");
    } finally {
      setIsSavingPost(false);
    }
  };

  const authHeaders = () => ({ "X-Admin-Token": getToken() });

  const requireDeletePassword = async (label) => {
    const pwd = window.prompt(`⚠️ Confirm delete: ${label}\n\nEnter delete password to proceed:`);
    if (!pwd) return false;
    try {
      await axios.post(`${API}/admin/verify-delete`, { password: pwd }, { headers: authHeaders() });
      return true;
    } catch {
      toast.error("Wrong password — delete cancelled");
      return false;
    }
  };

  const handleDeletePost = async (postId) => {
    if (!await requireDeletePassword("blog post")) return;
    try {
      await axios.delete(`${API}/blog/${postId}`);
      toast.success("Post deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      const res = await axios.post(`${API}/admin/login`, { username, password });
      const userRole = res.data.role || "viewer";
      const token = res.data.token || "";
      localStorage.setItem("admin_auth", "true");
      localStorage.setItem("admin_role", userRole);
      localStorage.setItem("admin_token", token);
      localStorage.setItem("admin_user", username.toLowerCase());
      setRole(userRole);
      setLoggedInUser(username.toLowerCase());
      setIsAuthenticated(true);
      toast.success("Login successful");
    } catch (error) {
      toast.error("Invalid credentials");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    const token = getToken();
    if (token) axios.post(`${API}/admin/logout`, {}, { headers: { "X-Admin-Token": token } }).catch(() => {});
    localStorage.removeItem("admin_auth");
    localStorage.removeItem("admin_role");
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    setIsAuthenticated(false);
    toast.success("Logged out");
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!['csv', 'pdf', 'json', 'xlsx'].includes(ext)) {
        toast.error("Only CSV, PDF, JSON, and XLSX files are allowed");
        return;
      }
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file || !uploadData.dataset_category) {
      toast.error("Please select a file and category");
      return;
    }

    setIsUploading(true);
    
    const formData = new FormData();
    formData.append("file", uploadData.file);
    formData.append("dataset_category", uploadData.dataset_category);
    formData.append("description", uploadData.description);
    formData.append("is_sample", uploadData.is_sample);

    try {
      await axios.post(`${API}/files/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("File uploaded successfully");
      setIsUploadOpen(false);
      setUploadData({ file: null, dataset_category: "", description: "", is_sample: true });
      fetchData();
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Upload error:", error);
      toast.error("Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!await requireDeletePassword("file")) return;
    
    try {
      await axios.delete(`${API}/files/${fileId}`);
      toast.success("File deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!await requireDeletePassword("buyer lead")) return;

    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    if (!await requireDeletePassword("vendor lead")) return;
    try {
      await axios.delete(`${API}/vendors/${vendorId}`);
      toast.success("Vendor deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete vendor");
    }
  };

  const handleDeleteAllLead = async (lead) => {
    if (!await requireDeletePassword("lead")) return;
    const endpoint = lead.lead_type === "vendor" ? "vendors" : "leads";
    try {
      await axios.delete(`${API}/${endpoint}/${lead.id}`);
      toast.success("Lead deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  const handleCall = async (lead) => {
    const rawMobile = (lead.mobile_number || "").replace(/\s/g, "");
    if (!rawMobile) { toast.error("No phone number for this lead"); return; }
    const rawCode = (lead.mobile_country_code || "").split("-")[0];
    const number = rawMobile.startsWith("+") ? rawMobile : `${rawCode || "+91"}${rawMobile}`;

    let agentNumber = localStorage.getItem("nd_agent_phone") || "";
    if (!agentNumber) {
      agentNumber = window.prompt("Enter your phone number (with country code, e.g. +919876543210). Vobiz will ring this number first, then connect you to the lead. This will be saved for future calls.");
      if (!agentNumber) return;
      localStorage.setItem("nd_agent_phone", agentNumber.trim());
    }

    try {
      await axios.post(`${API}/admin/call`, { lead_number: number, agent_number: agentNumber.trim() }, { headers: authHeaders() });
      toast.success("Call initiated — your phone will ring first");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to initiate call");
    }
  };

  const handleLeadStatus = async (leadId, status) => {
    try {
      await axios.patch(`${API}/leads/${leadId}/status`, { status }, { headers: authHeaders() });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleVendorStatus = async (vendorId, status) => {
    try {
      await axios.patch(`${API}/vendors/${vendorId}/status`, { status }, { headers: authHeaders() });
      toast.success(`Status updated to ${status}`);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleAllLeadStatus = async (lead, status) => {
    const endpoint = lead.lead_type === "vendor" ? "vendors" : "leads";
    try {
      await axios.patch(`${API}/${endpoint}/${lead.id}/status`, { status }, { headers: authHeaders() });
      toast.success(`Status → ${status}`);
      setAllLeads(prev => prev.map(l => l.id === lead.id ? { ...l, status } : l));
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleLeadNotes = async (lead) => {
    const notes = noteDraft.hasOwnProperty(lead.id) ? noteDraft[lead.id] : (lead.notes || "");
    try {
      await axios.patch(`${API}/all-leads/${lead.id}/notes`, { notes }, { headers: authHeaders() });
      toast.success("Notes saved");
      setAllLeads(prev => prev.map(l => l.id === lead.id ? { ...l, notes } : l));
      setNoteOpen(null);
    } catch {
      toast.error("Failed to save notes");
    }
  };

  const fetchUnmatched = async () => {
    setIsLoadingUnmatched(true);
    try {
      const res = await axios.get(`${API}/admin/vobiz-unmatched`, { headers: authHeaders() });
      setUnmatchedConvs(res.data);
    } catch {
      toast.error("Failed to fetch unmatched conversations");
    } finally {
      setIsLoadingUnmatched(false);
    }
  };

  const handleSyncVobiz = async () => {
    setIsSyncingVobiz(true);
    try {
      const res = await axios.post(`${API}/admin/sync-vobiz`, {}, { headers: authHeaders() });
      toast.success(`Vobiz sync done — ${res.data.updated} leads updated`);
      fetchData();
    } catch {
      toast.error("Vobiz sync failed");
    } finally {
      setIsSyncingVobiz(false);
    }
  };

  const exportToCSV = () => {
    const headers = ["Type", "Name", "Email", "Company", "Phone", "Country", "Status", "Source", "Date", "Notes"];
    const rows = filteredAllLeads.map(l => [
      l.lead_type,
      l.full_name,
      l.work_email,
      l.company || "",
      `${(l.mobile_country_code || "").split("-")[0]} ${l.mobile_number || ""}`.trim(),
      (l.mobile_country_code || "").split("-").slice(1).join("") || "",
      l.status || "pending",
      l.utm_source ? "meta" : (l.source || "organic"),
      l.created_at || "",
      l.notes || "",
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(`${API}/files/${file.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Kolkata'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const filteredAllLeads = allLeads.filter(lead => {
    const q = allLeadsFilter.search.toLowerCase();
    if (q && ![lead.full_name, lead.work_email, lead.company || ""].join(" ").toLowerCase().includes(q)) return false;
    if (allLeadsFilter.lead_type && lead.lead_type !== allLeadsFilter.lead_type) return false;
    if (allLeadsFilter.source) {
      const isMeta = Boolean(lead.utm_source);
      if (allLeadsFilter.source === "meta" && !isMeta) return false;
      if (allLeadsFilter.source === "organic" && isMeta) return false;
    }
    if (allLeadsFilter.status && lead.status !== allLeadsFilter.status) return false;
    if (allLeadsFilter.country) {
      const cc = (lead.mobile_country_code || "").toLowerCase();
      if (!cc.includes(allLeadsFilter.country.toLowerCase())) return false;
    }
    if (allLeadsFilter.campaign) {
      const c = lead.utm_campaign || "";
      if (allLeadsFilter.campaign === "seller_in-us-uk" && !c.includes("seller_in-us-uk")) return false;
      if (allLeadsFilter.campaign === "seller_africa" && !c.includes("seller_africa")) return false;
      if (allLeadsFilter.campaign === "cobol" && !(c.includes("cobol") || c === "cobol-seller")) return false;
      if (allLeadsFilter.campaign === "cad" && c !== "120250381290200331") return false;
      if (allLeadsFilter.campaign === "retargeting" && !c.includes("retargeting")) return false;
      if (allLeadsFilter.campaign === "organic" && c && c !== "organic") return false;
    }
    if (allLeadsFilter.platform) {
      if (allLeadsFilter.platform === "organic" && lead.utm_source) return false;
      if (allLeadsFilter.platform !== "organic" && lead.utm_source !== allLeadsFilter.platform) return false;
    }
    if (allLeadsFilter.wa === "replied" && !lead.has_replied) return false;
    if (allLeadsFilter.wa === "not_replied" && lead.has_replied) return false;
    if (allLeadsFilter.wa === "active" && !lead.wa_conversation_open) return false;
    return true;
  }).sort((a, b) => {
    if (allLeadsSort === "wa_activity") {
      const ta = a.last_wa_activity ? new Date(a.last_wa_activity).getTime() : 0;
      const tb = b.last_wa_activity ? new Date(b.last_wa_activity).getTime() : 0;
      return tb - ta;
    }
    if (allLeadsSort === "created_asc") return new Date(a.created_at) - new Date(b.created_at);
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4" data-testid="admin-login-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">S</span>
            </div>
            <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
            <p className="text-gray-500">S Chand AI Infrastructure</p>
          </div>

          <form onSubmit={handleLogin} className="p-8 rounded-2xl bg-[#121212] border border-white/5">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-gray-300">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="Enter username"
                  data-testid="admin-username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-300">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="Enter password"
                  data-testid="admin-password"
                />
              </div>
              <Button
                type="submit"
                disabled={isLoggingIn}
                className="w-full btn-primary"
                data-testid="admin-login-btn"
              >
                {isLoggingIn ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Logging in...
                  </>
                ) : (
                  "Login"
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <span className="text-white font-bold text-xl">S</span>
              </div>
              <div>
                <span className="text-white font-semibold">Admin Panel</span>
                <span className="text-gray-500 text-sm block -mt-1">{loggedInUser ? `${loggedInUser} · ${role}` : "S Chand AI"}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => {
                  const current = localStorage.getItem("nd_agent_phone") || "";
                  const num = window.prompt("Your phone number for click-to-call (e.g. +919876543210):", current);
                  if (num === null) return;
                  if (num.trim()) { localStorage.setItem("nd_agent_phone", num.trim()); toast.success("Phone number saved"); }
                  else { localStorage.removeItem("nd_agent_phone"); toast.success("Phone number cleared"); }
                }}
                variant="ghost"
                className="text-gray-400 hover:text-white text-xs"
                title="Set your phone number for click-to-call"
              >
                📞 My Phone
              </Button>
              {isAdmin && <Button
                onClick={async () => {
                  if (!window.confirm("Log out ALL users? Everyone will need to log in again.")) return;
                  try {
                    await axios.post(`${API}/admin/logout-all`, {}, { headers: authHeaders() });
                    toast.success("All users logged out");
                  } catch { toast.error("Failed"); }
                }}
                variant="ghost"
                className="text-orange-400 hover:text-orange-300 text-xs"
                title="Force logout all users"
              >
                Logout All
              </Button>}
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-gray-400 hover:text-white"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="p-6 rounded-xl bg-[#121212] border border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Database className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.files_count}</p>
                <p className="text-gray-500 text-sm">Total Files</p>
              </div>
            </div>
          </div>
          <div className="p-6 rounded-xl bg-[#121212] border border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.leads_count}</p>
                <p className="text-gray-500 text-sm">Total Leads</p>
              </div>
            </div>
          </div>
          <div className="p-6 rounded-xl bg-[#121212] border border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-3xl font-bold text-white">{stats.total_downloads}</p>
                <p className="text-gray-500 text-sm">Total Downloads</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-4 mb-6">
          {isAdmin && <button
            onClick={() => { setActiveTab("logs"); axios.get(`${API}/admin/logs`, { headers: authHeaders() }).then(r => setLogs(r.data)).catch(() => {}); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === "logs" ? "bg-white text-black" : "text-gray-400 hover:text-white"}`}
          >
            🗒 Logs
          </button>}
          {isAdmin && <button
            onClick={() => setActiveTab("files")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "files"
                ? "bg-white text-black"
                : "text-gray-400 hover:text-white"
            }`}
            data-testid="admin-tab-files"
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Files
          </button>}
          <button
            onClick={() => setActiveTab("leads")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "leads"
                ? "bg-white text-black"
                : "text-gray-400 hover:text-white"
            }`}
            data-testid="admin-tab-leads"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Buyers
            {leads.length > 0 && (
              <span className="ml-2 text-xs bg-white/10 text-gray-400 px-1.5 py-0.5 rounded-full">
                {leads.length}
              </span>
            )}
          </button>
          {isAdmin && <button
            onClick={() => setActiveTab("blog")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "blog"
                ? "bg-white text-black"
                : "text-gray-400 hover:text-white"
            }`}
            data-testid="admin-tab-blog"
          >
            <Newspaper className="w-4 h-4 inline mr-2" />
            Blog
          </button>}
          <button
            onClick={() => setActiveTab("vendors")}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "vendors"
                ? "bg-white text-black"
                : "text-gray-400 hover:text-white"
            }`}
            data-testid="admin-tab-vendors"
          >
            <Database className="w-4 h-4 inline mr-2" />
            Sellers
            {vendors.filter(v => v.status === "pending").length > 0 && (
              <span className="ml-2 text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">
                {vendors.filter(v => v.status === "pending").length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab("all-leads"); fetchUnmatched(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === "all-leads"
                ? "bg-white text-black"
                : "text-gray-400 hover:text-white"
            }`}
            data-testid="admin-tab-all-leads"
          >
            <Users className="w-4 h-4 inline mr-2" />
            All Leads
            <span className="ml-2 text-xs bg-white/10 text-gray-400 px-1.5 py-0.5 rounded-full">
              {allLeads.length}
            </span>
          </button>
        </div>

        {/* Content */}
        {activeTab === "logs" && (
          <div className="rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h2 className="text-lg font-semibold text-white">Activity Logs</h2>
            </div>
            {logs.length === 0 ? (
              <p className="text-gray-500 text-sm p-6">No activity logs yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-gray-400 text-xs uppercase">
                      <th className="text-left p-3 font-medium">Time</th>
                      <th className="text-left p-3 font-medium">User</th>
                      <th className="text-left p-3 font-medium">Action</th>
                      <th className="text-left p-3 font-medium">Target</th>
                      <th className="text-left p-3 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log, i) => (
                      <tr key={i} className={`border-b border-white/5 ${i % 2 === 0 ? "bg-white/[0.02]" : ""}`}>
                        <td className="p-3 text-gray-400 whitespace-nowrap">
                          {log.ts ? new Date(log.ts).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : "—"}
                        </td>
                        <td className="p-3 text-white font-medium">{log.username || "—"}</td>
                        <td className="p-3 text-blue-300">{log.action || "—"}</td>
                        <td className="p-3 text-gray-300">{log.target || "—"}</td>
                        <td className="p-3 text-gray-400 text-xs">{log.details || ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "files" && (
          <div className="rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Dataset Files</h2>
              <Button
                onClick={() => setIsUploadOpen(true)}
                className="btn-primary"
                data-testid="admin-upload-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload File
              </Button>
            </div>
            
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto" />
              </div>
            ) : files.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No files uploaded yet
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-400 border-r border-white/10">Filename</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Category</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Size</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Downloads</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Uploaded</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {files.map((file) => (
                    <TableRow key={file.id} className="border-white/10">
                      <TableCell className="text-white font-medium border-r border-white/10">
                        {file.original_filename}
                        {file.is_sample && (
                          <span className="ml-2 text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">
                            Sample
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-400 border-r border-white/10">{file.dataset_category}</TableCell>
                      <TableCell className="text-gray-400 border-r border-white/10">{formatFileSize(file.file_size)}</TableCell>
                      <TableCell className="text-gray-400 border-r border-white/10">{file.download_count}</TableCell>
                      <TableCell className="text-gray-400 border-r border-white/10">{formatDate(file.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => handleDownload(file)}
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-white"
                            data-testid={`download-${file.id}`}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          {isAdmin && <Button
                            onClick={() => handleDeleteFile(file.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300"
                            data-testid={`delete-file-${file.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {activeTab === "leads" && (
          <div className="rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <h2 className="text-lg font-semibold text-white">
                Buyer Submissions
                <span className="ml-2 text-sm font-normal text-gray-500">({leads.length} total)</span>
              </h2>
            </div>
            
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto" />
              </div>
            ) : leads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No leads yet
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {leads.map((lead) => {
                  const CAMPAIGN_NAMES = {"120250093237030331":"Africa Tier 1","120249845411780331":"Vendor IN/US/UK","120250071140900331":"Buyer","120250094408630331":"Retargeting","120250071151190331":"Buyer Coding","120250071151500331":"Buyer Enterprise","120250138223110331":"Seller IN-US-UK","120250138223200331":"Buyer IN-US-UK","120250381290200331":"CAD Seller"};
                  const UTM_SOURCE_LABELS = {"fb":"Facebook","ig":"Instagram","an":"Audience Network","meta":"Meta","organic":"Organic"};
                  const campaignLabel = lead.utm_campaign ? (CAMPAIGN_NAMES[lead.utm_campaign] || lead.utm_campaign) : null;
                  const sourceLabel = UTM_SOURCE_LABELS[lead.utm_source] || lead.utm_source;
                  return (
                  <div key={lead.id} className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-1">
                          <span className="text-white font-semibold">{lead.full_name}</span>
                          <span className="text-gray-500 text-sm">{lead.work_email}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                            lead.status === "contacted" ? "bg-green-500/20 text-green-400" :
                            lead.status === "closed" ? "bg-red-500/20 text-red-400" :
                            lead.status === "reviewing" ? "bg-blue-500/20 text-blue-400" :
                            "bg-amber-500/20 text-amber-400"
                          }`}>{lead.status || "pending"}</span>
                        </div>
                        <p className="text-gray-400 text-sm mb-2">{lead.company} · {lead.role}</p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {lead.use_case && (
                            <span className="text-xs bg-white/5 text-gray-300 px-2 py-0.5 rounded capitalize">{lead.use_case.replace(/_/g, " ")}</span>
                          )}
                          {lead.company_type && lead.company_type !== "other" && (
                            <span className="text-xs bg-white/5 text-gray-500 px-2 py-0.5 rounded capitalize">{lead.company_type.replace(/_/g, " ")}</span>
                          )}
                          {lead.dataset_interest && (
                            <span className="text-xs bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded">{lead.dataset_interest}</span>
                          )}
                          {lead.utm_content && (
                            <span className="text-xs bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded">{lead.utm_content}</span>
                          )}
                        </div>
                        {lead.message && (
                          <p className="text-gray-500 text-sm line-clamp-2">{lead.message}</p>
                        )}
                        <p className="text-gray-600 text-xs mt-1">
                          {formatDate(lead.created_at)} · {lead.mobile_country_code} {lead.mobile_number}
                          {lead.utm_source ? (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-mono bg-blue-500/15 text-blue-400">
                              {sourceLabel}{campaignLabel ? ` · ${campaignLabel}` : ""}
                            </span>
                          ) : lead.source === "organic" || !lead.source ? (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-mono bg-white/5 text-gray-500">Organic</span>
                          ) : null}
                          {lead.linkedin_url && (
                            <a href={lead.linkedin_url} target="_blank" rel="noopener noreferrer" className="ml-2 px-1.5 py-0.5 rounded text-xs font-mono bg-blue-600/20 text-blue-400 hover:text-blue-300">LinkedIn ↗</a>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isAdmin && <select
                          value={lead.status || "pending"}
                          onChange={e => handleLeadStatus(lead.id, e.target.value)}
                          className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1.5 cursor-pointer"
                        >
                          <option value="pending">Pending</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="contacted">Contacted</option>
                          <option value="closed">Closed</option>
                        </select>}
                        <Button
                          onClick={() => handleCall(lead)}
                          variant="ghost"
                          size="sm"
                          className="text-green-400 hover:text-green-300"
                          title="Call lead"
                        >
                          📞
                        </Button>
                        {isAdmin && <Button
                          onClick={() => handleDeleteLead(lead.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          data-testid={`delete-lead-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "blog" && (
          <div className="rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Blog Posts</h2>
              <Button onClick={openNewPost} className="btn-primary" data-testid="admin-new-post-btn">
                <Plus className="w-4 h-4 mr-2" />
                New Post
              </Button>
            </div>
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto" />
              </div>
            ) : blogPosts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No blog posts yet</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-400 border-r border-white/10">Title</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Category</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Status</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Date</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blogPosts.map((post) => (
                    <TableRow key={post.id} className="border-white/10">
                      <TableCell className="text-white font-medium max-w-xs truncate border-r border-white/10">
                        {post.title}
                      </TableCell>
                      <TableCell className="text-gray-400 border-r border-white/10">{post.category}</TableCell>
                      <TableCell className="border-r border-white/10">
                        {post.published ? (
                          <span className="flex items-center gap-1.5 text-green-400 text-xs font-mono">
                            <Eye className="w-3 h-3" /> Published
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-gray-500 text-xs font-mono">
                            <EyeOff className="w-3 h-3" /> Draft
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-gray-400 border-r border-white/10">
                        {post.published_at ? formatDate(post.published_at) : formatDate(post.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => openEditPost(post)}
                            variant="ghost"
                            size="sm"
                            className="text-gray-400 hover:text-white"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeletePost(post.id)}
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        )}

        {activeTab === "all-leads" && (
          <div className="flex gap-4 items-start">
          <div className="flex-1 min-w-0 rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-white">
                  All Leads
                  <span className="ml-2 text-sm font-normal text-gray-500">
                    ({filteredAllLeads.length}{filteredAllLeads.length !== allLeads.length ? ` of ${allLeads.length}` : ""} total)
                  </span>
                </h2>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={handleSyncVobiz}
                    disabled={isSyncingVobiz}
                    variant="ghost"
                    size="sm"
                    className="text-green-400 hover:text-green-300 flex items-center gap-1.5"
                    title="Pull latest WhatsApp reply status from Vobiz"
                  >
                    {isSyncingVobiz ? <Loader2 className="w-4 h-4 animate-spin" /> : "🔄"}
                    Sync WA
                  </Button>
                  <Button
                    onClick={exportToCSV}
                    variant="ghost"
                    size="sm"
                    className="text-gray-400 hover:text-white flex items-center gap-1.5"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Input
                  value={allLeadsFilter.search}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, search: e.target.value }))}
                  placeholder="Search name, email, company…"
                  className="bg-black/50 border-white/10 text-white text-xs placeholder:text-gray-600 h-8 flex-1 min-w-[160px]"
                />
                <select
                  value={allLeadsFilter.lead_type}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, lead_type: e.target.value }))}
                  className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="">All Types</option>
                  <option value="buyer">Buyer</option>
                  <option value="vendor">Vendor</option>
                </select>
                <select
                  value={allLeadsFilter.source}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, source: e.target.value }))}
                  className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="">All Sources</option>
                  <option value="meta">Meta Ads</option>
                  <option value="organic">Organic</option>
                </select>
                <select
                  value={allLeadsFilter.status}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, status: e.target.value }))}
                  className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="reviewing">Reviewing</option>
                  <option value="contacted">Contacted</option>
                  <option value="called">Called</option>
                  <option value="interested">Interested</option>
                  <option value="not_interested">Not Interested</option>
                  <option value="closed">Closed</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <Input
                  value={allLeadsFilter.country}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, country: e.target.value }))}
                  placeholder="Country…"
                  className="bg-black/50 border-white/10 text-white text-xs placeholder:text-gray-600 h-8 w-28"
                />
                <select
                  value={allLeadsFilter.campaign}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, campaign: e.target.value }))}
                  className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="">All Campaigns</option>
                  <option value="seller_in-us-uk">Seller IN-US-UK</option>
                  <option value="seller_africa">Africa</option>
                  <option value="cobol">COBOL</option>
                  <option value="cad">CAD Seller</option>
                  <option value="retargeting">Retargeting</option>
                  <option value="organic">Organic</option>
                </select>
                <select
                  value={allLeadsFilter.platform}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, platform: e.target.value }))}
                  className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="">All Platforms</option>
                  <option value="ig">Instagram</option>
                  <option value="fb">Facebook</option>
                  <option value="an">Audience Network</option>
                  <option value="organic">Organic</option>
                </select>
                <select
                  value={allLeadsFilter.wa}
                  onChange={e => setAllLeadsFilter(f => ({ ...f, wa: e.target.value }))}
                  className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="">All WA</option>
                  <option value="replied">Replied</option>
                  <option value="not_replied">No Reply</option>
                  <option value="active">Active Conv.</option>
                </select>
                <select
                  value={allLeadsSort}
                  onChange={e => setAllLeadsSort(e.target.value)}
                  className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer"
                >
                  <option value="created_desc">Newest first</option>
                  <option value="created_asc">Oldest first</option>
                  <option value="wa_activity">Last WA activity</option>
                </select>
                {(allLeadsFilter.search || allLeadsFilter.lead_type || allLeadsFilter.source || allLeadsFilter.status || allLeadsFilter.country || allLeadsFilter.campaign || allLeadsFilter.platform || allLeadsFilter.wa) && (
                  <Button
                    onClick={() => setAllLeadsFilter({ search: "", lead_type: "", source: "", status: "", country: "", campaign: "", platform: "", wa: "" })}
                    variant="ghost"
                    size="sm"
                    className="text-gray-500 hover:text-white h-8 px-2"
                    title="Clear filters"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto" />
              </div>
            ) : filteredAllLeads.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                {allLeads.length === 0 ? "No leads yet" : "No leads match the current filters"}
              </div>
            ) : (
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-400 border-r border-white/10">Type</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Name</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Email</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Company</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">WhatsApp</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Status</TableHead>
                    <TableHead className="text-gray-400 border-r border-white/10">Date</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAllLeads.flatMap((lead) => {
                    const rows = [
                      <TableRow key={lead.id} className="border-white/10">
                        <TableCell className="border-r border-white/10">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                            lead.lead_type === "vendor"
                              ? "bg-purple-500/20 text-purple-400"
                              : "bg-green-500/20 text-green-400"
                          }`}>
                            {lead.lead_type === "vendor" ? "vendor" : "buyer"}
                          </span>
                        </TableCell>
                        <TableCell className="text-white font-medium border-r border-white/10">{lead.full_name}</TableCell>
                        <TableCell className="text-gray-400 text-xs border-r border-white/10 break-all max-w-[160px]">{lead.work_email}</TableCell>
                        <TableCell className="text-gray-400 text-xs border-r border-white/10 break-words max-w-[120px]">{lead.company || "—"}</TableCell>
                        <TableCell className="border-r border-white/10">
                          <div className="flex flex-col gap-1">
                            {lead.has_replied ? (
                              <span className="text-xs px-1.5 py-0.5 rounded font-mono bg-green-500/20 text-green-400 w-fit">✓ Replied</span>
                            ) : (
                              <span className="text-xs px-1.5 py-0.5 rounded font-mono bg-white/5 text-gray-600 w-fit">No reply</span>
                            )}
                            {lead.wa_conversation_open && (
                              <span className="text-xs px-1.5 py-0.5 rounded font-mono bg-blue-500/20 text-blue-400 w-fit">● Active</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="border-r border-white/10">
                          <select
                            value={lead.status || "pending"}
                            onChange={isAdmin ? (e => handleAllLeadStatus(lead, e.target.value)) : undefined}
                            disabled={!isAdmin}
                            className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {lead.lead_type === "vendor" ? (
                              <>
                                <option value="pending">Pending</option>
                                <option value="reviewing">Reviewing</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                              </>
                            ) : (
                              <>
                                <option value="pending">Pending</option>
                                <option value="reviewing">Reviewing</option>
                                <option value="contacted">Contacted</option>
                                <option value="called">Called</option>
                                <option value="interested">Interested</option>
                                <option value="not_interested">Not Interested</option>
                                <option value="closed">Closed</option>
                              </>
                            )}
                          </select>
                        </TableCell>
                        <TableCell className="text-gray-400 text-xs border-r border-white/10">
                          <div>{formatDate(lead.created_at)}</div>
                          {lead.last_wa_activity && (
                            <div className="text-green-500/70 mt-0.5">WA: {formatDate(lead.last_wa_activity)}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-center gap-0.5">
                            {isAdmin && <Button
                              onClick={() => {
                                setNoteOpen(noteOpen === lead.id ? null : lead.id);
                                if (!noteDraft.hasOwnProperty(lead.id)) {
                                  setNoteDraft(p => ({ ...p, [lead.id]: lead.notes || "" }));
                                }
                              }}
                              variant="ghost"
                              size="sm"
                              className={`h-7 w-7 p-0 ${lead.notes ? "text-yellow-400 hover:text-yellow-300" : "text-gray-500 hover:text-gray-300"}`}
                              title={lead.notes ? "Edit notes" : "Add notes"}
                            >
                              📝
                            </Button>}
                            <Button
                              onClick={() => handleCall(lead)}
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-green-400 hover:text-green-300"
                              title="Call lead"
                            >
                              📞
                            </Button>
                            {isAdmin && <Button
                              onClick={() => handleDeleteAllLead(lead)}
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ];
                    if (noteOpen === lead.id) {
                      rows.push(
                        <TableRow key={`${lead.id}-note`} className="border-white/5 bg-white/[0.02]">
                          <TableCell colSpan={8} className="pb-3 pt-1 px-4">
                            <div className="flex gap-2 items-end">
                              <Textarea
                                value={noteDraft.hasOwnProperty(lead.id) ? noteDraft[lead.id] : (lead.notes || "")}
                                onChange={isAdmin ? (e => setNoteDraft(p => ({ ...p, [lead.id]: e.target.value }))) : undefined}
                                readOnly={!isAdmin}
                                className="bg-black/50 border-white/10 text-white text-xs placeholder:text-gray-600 flex-1"
                                placeholder={isAdmin ? "Add notes about this lead…" : (lead.notes || "No notes")}
                                rows={2}
                              />
                              {isAdmin && <Button
                                size="sm"
                                className="btn-primary shrink-0"
                                onClick={() => handleLeadNotes(lead)}
                              >
                                Save
                              </Button>}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    }
                    return rows;
                  })}
                </TableBody>
              </Table>
              </div>
            )}
          </div>

          {/* WA Unmatched right panel */}
          <div className="w-72 shrink-0 rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
            <div className="p-3 border-b border-white/5 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">WA Unmatched</h3>
                <p className="text-xs text-gray-500">Messaged but no form</p>
              </div>
              <Button
                onClick={fetchUnmatched}
                disabled={isLoadingUnmatched}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white"
                title="Refresh"
              >
                {isLoadingUnmatched ? <Loader2 className="w-3 h-3 animate-spin" /> : "🔄"}
              </Button>
            </div>
            <div className="overflow-y-auto max-h-[600px]">
              {isLoadingUnmatched ? (
                <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-gray-500 mx-auto" /></div>
              ) : unmatchedConvs.length === 0 ? (
                <div className="p-6 text-center text-gray-600 text-xs">No unmatched conversations</div>
              ) : (
                <div className="divide-y divide-white/5">
                  {unmatchedConvs.map((conv, i) => (
                    <div key={i} className="p-3">
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${conv.status === "open" ? "bg-green-400" : "bg-gray-600"}`} />
                        <span className="text-white text-xs font-mono">{conv.phone}</span>
                      </div>
                      <div className="text-gray-500 text-xs mb-1">
                        {conv.last_message_at ? formatDate(conv.last_message_at) : "—"}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${conv.status === "open" ? "bg-green-500/20 text-green-400" : "bg-white/5 text-gray-500"}`}>
                          {conv.status === "open" ? "Open" : "Closed"}
                        </span>
                        {conv.category === "service" && (
                          <span className="text-xs px-1.5 py-0.5 rounded font-mono bg-blue-500/20 text-blue-400">Replied</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          </div>
        )}

        {activeTab === "vendors" && (
          <div className="rounded-xl bg-[#121212] border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                Seller Applications
                <span className="ml-2 text-sm font-normal text-gray-500">({vendors.length} total)</span>
              </h2>
            </div>
            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto" />
              </div>
            ) : vendors.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No seller applications yet</div>
            ) : (
              <div className="divide-y divide-white/5">
                {vendors.map((vendor) => {
                  const CAMPAIGN_NAMES = {"120250093237030331":"Africa Tier 1","120249845411780331":"Vendor IN/US/UK","120250071140900331":"Buyer","120250094408630331":"Retargeting","120250071151190331":"Buyer Coding","120250071151500331":"Buyer Enterprise","120250138223110331":"Seller IN-US-UK","120250138223200331":"Buyer IN-US-UK","120250381290200331":"CAD Seller"};
                  const UTM_SOURCE_LABELS = {"fb":"Facebook","ig":"Instagram","an":"Audience Network","meta":"Meta","organic":"Organic"};
                  const campaignLabel = vendor.utm_campaign ? (CAMPAIGN_NAMES[vendor.utm_campaign] || vendor.utm_campaign) : null;
                  const sourceLabel = UTM_SOURCE_LABELS[vendor.utm_source] || vendor.utm_source;
                  return (
                  <div key={vendor.id} className="p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap mb-1">
                          <span className="text-white font-semibold">{vendor.full_name}</span>
                          <span className="text-gray-500 text-sm">{vendor.work_email}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-mono ${
                            vendor.status === "approved" ? "bg-green-500/20 text-green-400" :
                            vendor.status === "rejected" ? "bg-red-500/20 text-red-400" :
                            vendor.status === "reviewing" ? "bg-blue-500/20 text-blue-400" :
                            "bg-amber-500/20 text-amber-400"
                          }`}>{vendor.status}</span>
                        </div>
                        <p className="text-gray-400 text-sm mb-2">{vendor.company} · {vendor.role}</p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {(vendor.domains || []).map(d => (
                            <span key={d} className="text-xs bg-white/5 text-gray-300 px-2 py-0.5 rounded">{d.replace(/_/g, " ")}</span>
                          ))}
                          {(vendor.data_formats || []).map(f => (
                            <span key={f} className="text-xs bg-white/5 text-gray-500 px-2 py-0.5 rounded">{f}</span>
                          ))}
                          {vendor.data_volume && (
                            <span className="text-xs bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded">{vendor.data_volume}</span>
                          )}
                          {vendor.utm_content && (
                            <span className="text-xs bg-blue-500/10 text-blue-300 px-2 py-0.5 rounded">{vendor.utm_content}</span>
                          )}
                        </div>
                        <p className="text-gray-500 text-sm line-clamp-2">{vendor.description}</p>
                        <p className="text-gray-600 text-xs mt-1">
                          {formatDate(vendor.created_at)} · {vendor.mobile_country_code} {vendor.mobile_number}
                          {vendor.utm_source ? (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-mono bg-blue-500/15 text-blue-400">
                              {sourceLabel}{campaignLabel ? ` · ${campaignLabel}` : ""}
                            </span>
                          ) : vendor.source === "organic" || !vendor.source ? (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-mono bg-white/5 text-gray-500">Organic</span>
                          ) : null}
                          {vendor.linkedin_url && (
                            <a href={vendor.linkedin_url} target="_blank" rel="noopener noreferrer" className="ml-2 px-1.5 py-0.5 rounded text-xs font-mono bg-blue-600/20 text-blue-400 hover:text-blue-300">LinkedIn ↗</a>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isAdmin && <select
                          value={vendor.status}
                          onChange={e => handleVendorStatus(vendor.id, e.target.value)}
                          className="text-xs bg-white/5 border border-white/10 text-gray-300 rounded px-2 py-1.5 cursor-pointer"
                        >
                          <option value="pending">Pending</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="approved">Approved</option>
                          <option value="rejected">Rejected</option>
                        </select>}
                        <Button
                          onClick={() => handleCall(vendor)}
                          variant="ghost"
                          size="sm"
                          className="text-green-400 hover:text-green-300"
                          title="Call vendor"
                        >
                          📞
                        </Button>
                        {isAdmin && <Button
                          onClick={() => handleDeleteVendor(vendor.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>}
                      </div>
                    </div>
                    {vendor.notes && (
                      <p className="text-gray-600 text-xs mt-2 italic">Notes: {vendor.notes}</p>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Blog Post Dialog */}
      <Dialog open={isBlogFormOpen} onOpenChange={setIsBlogFormOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Post" : "New Blog Post"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSavePost} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2 col-span-2">
                <Label className="text-gray-300">Title *</Label>
                <Input
                  value={blogForm.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setBlogForm((f) => ({
                      ...f,
                      title,
                      slug: f.slug || slugify(title),
                    }));
                  }}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="Post title"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Slug *</Label>
                <Input
                  value={blogForm.slug}
                  onChange={(e) => setBlogForm((f) => ({ ...f, slug: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white font-mono text-sm"
                  placeholder="post-slug"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Category</Label>
                <Input
                  value={blogForm.category}
                  onChange={(e) => setBlogForm((f) => ({ ...f, category: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="Research"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Excerpt *</Label>
              <Textarea
                value={blogForm.excerpt}
                onChange={(e) => setBlogForm((f) => ({ ...f, excerpt: e.target.value }))}
                className="bg-black/50 border-white/10 text-white placeholder:text-gray-600"
                placeholder="Short summary shown in the blog list…"
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Content * (Markdown)</Label>
              <Textarea
                value={blogForm.content}
                onChange={(e) => setBlogForm((f) => ({ ...f, content: e.target.value }))}
                className="bg-black/50 border-white/10 text-white placeholder:text-gray-600 font-mono text-sm"
                placeholder="Write your post in Markdown…"
                rows={14}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Tags (comma-separated)</Label>
                <Input
                  value={blogForm.tags}
                  onChange={(e) => setBlogForm((f) => ({ ...f, tags: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                  placeholder="GRPO, JEE, benchmark"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300">Author</Label>
                <Input
                  value={blogForm.author}
                  onChange={(e) => setBlogForm((f) => ({ ...f, author: e.target.value }))}
                  className="bg-black/50 border-white/10 text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-300">Hugging Face ref (optional)</Label>
              <Input
                value={blogForm.hf_model_ref}
                onChange={(e) => setBlogForm((f) => ({ ...f, hf_model_ref: e.target.value }))}
                className="bg-black/50 border-white/10 text-white font-mono text-sm"
                placeholder="Nalandadata/nalanda-qwen-7b-grpo or full URL"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="blog_published"
                checked={blogForm.published}
                onChange={(e) => setBlogForm((f) => ({ ...f, published: e.target.checked }))}
                className="w-4 h-4 rounded border-white/10 bg-black/50"
              />
              <Label htmlFor="blog_published" className="text-gray-300">
                Publish immediately
              </Label>
            </div>
            <Button type="submit" disabled={isSavingPost} className="w-full btn-primary">
              {isSavingPost ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                editingPost ? "Update Post" : "Create Post"
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload Dataset File</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleUpload} className="space-y-4">
            {/* File Upload Zone */}
            <div
              onClick={() => fileInputRef.current?.click()}
              className="upload-zone rounded-lg p-8 text-center cursor-pointer"
            >
              {uploadData.file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-blue-400" />
                  <div className="text-left">
                    <p className="text-white font-medium">{uploadData.file.name}</p>
                    <p className="text-gray-500 text-sm">{formatFileSize(uploadData.file.size)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setUploadData({ ...uploadData, file: null });
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400">Click to select file</p>
                  <p className="text-gray-600 text-sm">CSV, PDF, JSON, XLSX</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.pdf,.json,.xlsx"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />

            {/* Category */}
            <div className="space-y-2">
              <Label className="text-gray-300">Dataset Category *</Label>
              <Select
                value={uploadData.dataset_category}
                onValueChange={(value) => setUploadData({ ...uploadData, dataset_category: value })}
              >
                <SelectTrigger className="bg-black/50 border-white/10 text-white" data-testid="upload-category">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  {datasetCategories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} className="text-white hover:bg-white/10">
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label className="text-gray-300">Description</Label>
              <Textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })}
                className="bg-black/50 border-white/10 text-white placeholder:text-gray-600"
                placeholder="Optional description..."
                data-testid="upload-description"
              />
            </div>

            {/* Is Sample */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_sample"
                checked={uploadData.is_sample}
                onChange={(e) => setUploadData({ ...uploadData, is_sample: e.target.checked })}
                className="w-4 h-4 rounded border-white/10 bg-black/50 text-blue-500"
                data-testid="upload-is-sample"
              />
              <Label htmlFor="is_sample" className="text-gray-300">
                Available as sample download
              </Label>
            </div>

            <Button
              type="submit"
              disabled={isUploading}
              className="w-full btn-primary"
              data-testid="upload-submit"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload File
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
